import React, { Fragment, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import Content from '../../components/Content';
import SelectBox from '../../components/SelectBox';
import MultiSelectDropdown from '../../components/MultiSelectDropdown';
import SwitchBox from '../../components/SwitchBox';
import Button from '../../components/Button';
import Loader from '../../components/Loader';
import NetworkError from '../../components/Error';
import Table from '../../components/Table';
import ScheduleCard from '../../components/ScheduleCard';
import ScheduleForm from '../../components/ScheduleForm';
import EmailManagementModal from '../../components/Modal/EmailManagementModal';
import Pagination from '../../components/Pagination';
import usePagination from '../../components/Pagination/usePagination';

import { 
	getSecurityReportsMetrics,
	generateSecurityReport, 
	getReportHistory,
	downloadReport,
	getReportSchedules,
	createReportSchedule,
	updateReportSchedule,
	deleteReportSchedule,
	toggleReportSchedule
} from '../../redux/actions/securityReportsActions';
import { clearReportGenerating, updateReportCount } from '../../redux/reducers/reportReducers';
import {
	getUserEmails,
	addUserEmail,
	deleteUserEmail,
	setPrimaryEmail
} from '../../redux/actions/userEmailActions';
import { ErrorCodes } from '../../utils/errorCodes';
import { formatFrontendSupportError } from '../../utils/frontendErrorFormat';

import classes from './SecurityReports.module.css';

const FRONTEND_SECURITY_REPORTS_ERROR_CODES = {
	INITIAL_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	METRICS_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	REPORT_HISTORY_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	SCHEDULES_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	USER_EMAILS_LOAD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	POLLING_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	REPORT_GENERATION_VALIDATION_FAILED: ErrorCodes.API_INVALID_PARAMETER,
	REPORT_GENERATION_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	EMAIL_ADD_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	EMAIL_DELETE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	EMAIL_SET_PRIMARY_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	SCHEDULE_CREATE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	SCHEDULE_UPDATE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	SCHEDULE_DELETE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
	SCHEDULE_TOGGLE_FAILED: ErrorCodes.API_INTERNAL_ERROR,
};

const buildSecurityReportsSupportError = (errorCode, pageInfo, sourceMessage) =>
	formatFrontendSupportError({
		errorCode,
		pageInfo,
		sourceMessage,
	});

const getErrorMessage = (source, fallbackMessage) => {
	if (typeof source === 'string' && source.trim()) {
		return source.trim();
	}

	if (source && typeof source === 'object') {
		if (typeof source.msg === 'string' && source.msg.trim()) {
			return source.msg.trim();
		}
		if (typeof source.message === 'string' && source.message.trim()) {
			return source.message.trim();
		}
	}

	return fallbackMessage;
};

const SecurityReports = () => {
	const dispatch = useDispatch();

	// Redux state for report generation status
	const { isGenerating } = useSelector((state) => state.reportStatus);

	// Loading and error states
	const [initialLoading, setInitialLoading] = useState(true);
	const [hasError, setHasError] = useState(false);

	// Data states
	const [reportHistory, setReportHistory] = useState([]);
	const [availableWatchers, setAvailableWatchers] = useState([]);
	const [availableClients, setAvailableClients] = useState([]);
	const [schedules, setSchedules] = useState([]);

	// Filter states
	const [selectedClient, setSelectedClient] = useState(''); // '' = All Clients
	const [selectedWatchers, setSelectedWatchers] = useState([]); // Array of watcher IDs
	const [timeFrame, setTimeFrame] = useState('7'); // days
	const [customStartDate, setCustomStartDate] = useState('');
	const [customEndDate, setCustomEndDate] = useState('');
	const [showSubcategories, setShowSubcategories] = useState(false);
	const [emailReport, setEmailReport] = useState(false); // Whether to email manual reports
	const [reportFilter, setReportFilter] = useState('all'); // 'all', 'manual', 'scheduled'

	// Schedule modal states
	const [isScheduleFormOpen, setIsScheduleFormOpen] = useState(false);
	const [editingSchedule, setEditingSchedule] = useState(null);
	const [isSchedulesSectionExpanded, setIsSchedulesSectionExpanded] = useState(false);

	// Email management states
	const [userEmails, setUserEmails] = useState([]);
	const [showEmailModal, setShowEmailModal] = useState(false);
	const [supportErrorMessage, setSupportErrorMessage] = useState('');
	const [copiedSupportError, setCopiedSupportError] = useState(false);

	const setSecurityReportsSupportError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		setSupportErrorMessage(buildSecurityReportsSupportError(errorCode, pageInfo, sourceMessage));
		setCopiedSupportError(false);
	}, []);

	const clearSecurityReportsSupportError = React.useCallback((force = false) => {
		if (!force) {
			return;
		}
		setSupportErrorMessage('');
		setCopiedSupportError(false);
	}, []);

	const handleCopySupportError = React.useCallback(async () => {
		if (!supportErrorMessage) return;
		try {
			await navigator.clipboard.writeText(supportErrorMessage);
			setCopiedSupportError(true);
			setTimeout(() => setCopiedSupportError(false), 2000);
		} catch (error) {
			setCopiedSupportError(false);
		}
	}, [supportErrorMessage]);

	const notifySecurityReportsError = React.useCallback((errorCode, pageInfo, sourceMessage) => {
		const formattedError = buildSecurityReportsSupportError(errorCode, pageInfo, sourceMessage);
		setSupportErrorMessage(formattedError);
		setCopiedSupportError(false);
		toast.error(formattedError);
	}, []);

	// Fetch schedules function
	const fetchSchedules = React.useCallback(async () => {
		try {
			const data = await dispatch(getReportSchedules());
			if (data && data.status) {
				setSchedules(data.schedules || []);
				return { status: true };
			}

			setSecurityReportsSupportError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULES_LOAD_FAILED,
				'Security Reports Page / Schedules Load Failed',
				getErrorMessage(data, 'Schedules request did not return success')
			);
			return { status: false, msg: getErrorMessage(data, 'Schedules request did not return success') };
		} catch (error) {
			setSecurityReportsSupportError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULES_LOAD_FAILED,
				'Security Reports Page / Schedules Request Error',
				getErrorMessage(error, 'Error fetching report schedules')
			);
			return { status: false, msg: getErrorMessage(error, 'Error fetching report schedules') };
		}
	}, [dispatch, setSecurityReportsSupportError]);

	// Fetch user emails function
	const fetchUserEmails = React.useCallback(async () => {
		try {
			const data = await dispatch(getUserEmails());
			if (data && data.status) {
				setUserEmails(data.emails || []);
				return { status: true };
			}

			setSecurityReportsSupportError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.USER_EMAILS_LOAD_FAILED,
				'Security Reports Page / User Emails Load Failed',
				getErrorMessage(data, 'User emails request did not return success')
			);
			return { status: false, msg: getErrorMessage(data, 'User emails request did not return success') };
		} catch (error) {
			setSecurityReportsSupportError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.USER_EMAILS_LOAD_FAILED,
				'Security Reports Page / User Emails Request Error',
				getErrorMessage(error, 'Error fetching user emails')
			);
			return { status: false, msg: getErrorMessage(error, 'Error fetching user emails') };
		}
	}, [dispatch, setSecurityReportsSupportError]);

	// Initial data fetch
	useEffect(() => {
		const fetchInitialData = async () => {
			setInitialLoading(true);
			setHasError(false);

			try {
				// Fetch metrics to get available watchers
				const metricsData = await dispatch(getSecurityReportsMetrics());
				if (metricsData && metricsData.status) {
					setAvailableWatchers(metricsData.monitoredAssets?.watchers || []);
					setAvailableClients(metricsData.monitoredAssets?.companies || []);
				} else {
					setSecurityReportsSupportError(
						FRONTEND_SECURITY_REPORTS_ERROR_CODES.METRICS_LOAD_FAILED,
						'Security Reports Page / Metrics Load Failed',
						getErrorMessage(metricsData, 'Metrics request did not return success')
					);
				}

				// Fetch report history
				const historyData = await dispatch(getReportHistory());
				if (historyData && historyData.status) {
					const reports = historyData.reports || [];
					setReportHistory(reports);
					// Update report count in Redux
					dispatch(updateReportCount(reports.length));
				} else {
					setHasError(true);
					setSecurityReportsSupportError(
						FRONTEND_SECURITY_REPORTS_ERROR_CODES.REPORT_HISTORY_LOAD_FAILED,
						'Security Reports Page / Report History Load Failed',
						getErrorMessage(historyData, 'Report history request did not return success')
					);
				}

				// Fetch schedules
				await fetchSchedules();

				// Fetch user emails
				await fetchUserEmails();
			} catch (error) {
				setHasError(true);
				setSecurityReportsSupportError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.INITIAL_LOAD_FAILED,
					'Security Reports Page / Initial Data Request Error',
					getErrorMessage(error, 'Error fetching initial data')
				);
			} finally {
				setInitialLoading(false);
			}
		};

		fetchInitialData();
	}, [dispatch, fetchSchedules, fetchUserEmails, setSecurityReportsSupportError]);

	// Polling mechanism to detect report completion
	useEffect(() => {
		if (!isGenerating) {
			return;
		}

		let pollingInterval;
		let lastKnownCount = reportHistory.length;
		let pollAttempts = 0;
		const maxPollAttempts = 40; // ~10 minutes at 15s intervals

		const pollForCompletion = async () => {
			pollAttempts += 1;
			if (pollAttempts > maxPollAttempts) {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.POLLING_FAILED,
					'Security Reports Page / Report Polling Timeout',
					'Report generation timed out. Please check report history and try again if needed.'
				);
				dispatch(clearReportGenerating());
				if (pollingInterval) {
					clearInterval(pollingInterval);
				}
				return;
			}

			try {
				const historyData = await dispatch(getReportHistory());
				if (historyData && historyData.status) {
					const reports = historyData.reports || [];
					const newCount = reports.length;

					// Check if a new report was added
					if (newCount > lastKnownCount) {
						// Find the NEW report by comparing with the old list
						const oldReportIds = new Set(reportHistory.map((report) => report.reportId));
						const newReports = reports.filter((report) => !oldReportIds.has(report.reportId));

						// Get the most recently generated new report
						const newestReport = newReports.length > 0
							? newReports[0] // Since backend orders by GeneratedDate DESC
							: reports[0]; // Fallback to first report

						const reportStatus = (newestReport?.status || '').toLowerCase();
						if (reportStatus && reportStatus !== 'generated') {
							notifySecurityReportsError(
								FRONTEND_SECURITY_REPORTS_ERROR_CODES.POLLING_FAILED,
								'Security Reports Page / Report Completion Status',
								`Report completed with status: ${newestReport?.status || 'Unknown'}`
							);
						} else {
							toast.success(
								`Report generated successfully! ${newestReport?.reportTitle || 'Security Report'} - Click Download to view.`,
								{ duration: 6000 }
							);
						}

						// Update report history
						setReportHistory(reports);

						// Clear generating state
						dispatch(clearReportGenerating());
						dispatch(updateReportCount(newCount));
					}

					lastKnownCount = newCount;
				} else {
					setSecurityReportsSupportError(
						FRONTEND_SECURITY_REPORTS_ERROR_CODES.POLLING_FAILED,
						'Security Reports Page / Report Polling Failed',
						getErrorMessage(historyData, 'Polling request did not return success')
					);
				}
			} catch (error) {
				setSecurityReportsSupportError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.POLLING_FAILED,
					'Security Reports Page / Report Polling Request Error',
					getErrorMessage(error, 'Error polling for report completion')
				);
			}
		};

		// Poll every 15 seconds
		pollingInterval = setInterval(pollForCompletion, 15000);

		// Also poll immediately when starting
		pollForCompletion();

		// Cleanup on unmount or when generating stops
		return () => {
			if (pollingInterval) {
				clearInterval(pollingInterval);
			}
		};
	}, [dispatch, isGenerating, reportHistory, notifySecurityReportsError, setSecurityReportsSupportError]);

	// Handle email toggle. Enabling is always allowed: with no personal recipients
	// (Manage Email Recipients), the backend falls back to the Recipient(s)
	// configured in Settings — the air-gapped case where users are username-only.
	// We still nudge the user toward the recipients modal, but don't force it off.
	const handleEmailToggle = (checked) => {
		if (checked && userEmails.length === 0) {
			setShowEmailModal(true); // offer to add a personal recipient (optional)
		}
		setEmailReport(checked);
	};

	// Email management handlers
	const handleAddEmail = async (emailAddress) => {
		let result;
		try {
			result = await dispatch(addUserEmail(emailAddress));
		} catch (error) {
			const sourceMessage = getErrorMessage(error, 'Failed to add email');
			notifySecurityReportsError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.EMAIL_ADD_FAILED,
				'Security Reports Page / Add Email Request Error',
				sourceMessage
			);
			throw new Error(sourceMessage);
		}

		if (result && result.status) {
			toast.success('Email added successfully');
			await fetchUserEmails();
			return;
		}

		const sourceMessage = getErrorMessage(result, 'Failed to add email');
		notifySecurityReportsError(
			FRONTEND_SECURITY_REPORTS_ERROR_CODES.EMAIL_ADD_FAILED,
			'Security Reports Page / Add Email Failed',
			sourceMessage
		);
		throw new Error(sourceMessage);
	};

	const handleDeleteEmail = async (emailId) => {
		if (!window.confirm('Are you sure you want to delete this email address?')) {
			return;
		}
		
		try {
			const result = await dispatch(deleteUserEmail(emailId));
			if (result && result.status) {
				toast.success('Email deleted successfully');
				await fetchUserEmails();
				// If no emails left, turn off email toggle
				const updatedEmails = userEmails.filter(e => e.id !== emailId);
				if (updatedEmails.length === 0) {
					setEmailReport(false);
				}
			} else {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.EMAIL_DELETE_FAILED,
					'Security Reports Page / Delete Email Failed',
					getErrorMessage(result, 'Failed to delete email')
				);
			}
		} catch (error) {
			notifySecurityReportsError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.EMAIL_DELETE_FAILED,
				'Security Reports Page / Delete Email Request Error',
				getErrorMessage(error, 'Failed to delete email')
			);
		}
	};

	const handleSetPrimary = async (emailId) => {
		try {
			const result = await dispatch(setPrimaryEmail(emailId));
			if (result && result.status) {
				toast.success('Primary email updated');
				await fetchUserEmails();
			} else {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.EMAIL_SET_PRIMARY_FAILED,
					'Security Reports Page / Set Primary Email Failed',
					getErrorMessage(result, 'Failed to set primary email')
				);
			}
		} catch (error) {
			notifySecurityReportsError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.EMAIL_SET_PRIMARY_FAILED,
				'Security Reports Page / Set Primary Email Request Error',
				getErrorMessage(error, 'Failed to set primary email')
			);
		}
	};

	// Generate PDF report
	const handleGenerateReport = async () => {
		// Validate custom date range if selected
		if (timeFrame === 'custom') {
			if (!customStartDate || !customEndDate) {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.REPORT_GENERATION_VALIDATION_FAILED,
					'Security Reports Page / Generate Report Date Range Incomplete',
					'Please select both start and end dates'
				);
				return;
			}
			if (new Date(customStartDate) > new Date(customEndDate)) {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.REPORT_GENERATION_VALIDATION_FAILED,
					'Security Reports Page / Generate Report Invalid Date Range',
					'Start date must be before end date'
				);
				return;
			}
		}

		// Emailing no longer requires a per-account recipient: the backend falls
		// back to the Settings Recipient(s) and reports the outcome in its message.

		try {
			const formData = new FormData();

			// Calculate date range based on time frame
			let start, end;
			if (timeFrame === 'custom') {
				start = customStartDate;
				end = customEndDate;
			} else {
				end = new Date();
				start = new Date();
				start.setDate(start.getDate() - parseInt(timeFrame, 10));
				start = start.toISOString().split('T')[0];
				end = end.toISOString().split('T')[0];
			}
			
			formData.append('start_date', start);
			formData.append('end_date', end);

			// Add watcher filter (comma-separated list of IDs)
			if (selectedWatchers.length > 0) {
				formData.append('watchers', selectedWatchers.join(','));
			}

			// Add subcategory breakdown option
			formData.append('show_subcategories', showSubcategories ? 'true' : 'false');

			// Add email option for manual reports
			formData.append('send_email', emailReport ? 'true' : 'false');

			// Pass current report count for tracking
			const currentReportCount = reportHistory.length;
			const data = await dispatch(generateSecurityReport(formData, currentReportCount));
			
			if (data && data.status) {
				toast.success('Report generation started! You will be notified when it completes.', {
					duration: 4000
				});
				// Note: We don't refresh history or download here - polling will handle it
			} else if (data && data.requiresEmail) {
				// Special case: user needs to configure email
				setShowEmailModal(true);
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.REPORT_GENERATION_VALIDATION_FAILED,
					'Security Reports Page / Generate Report Requires Email Configuration',
					getErrorMessage(data, 'Please add an email address to receive reports')
				);
			} else {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.REPORT_GENERATION_FAILED,
					'Security Reports Page / Generate Report Failed',
					getErrorMessage(data, 'Failed to generate report')
				);
			}
		} catch (error) {
			notifySecurityReportsError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.REPORT_GENERATION_FAILED,
				'Security Reports Page / Generate Report Request Error',
				getErrorMessage(error, 'Error generating report')
			);
		}
	};

	// ===== Schedule Management Handlers =====
	
	const handleCreateSchedule = () => {
		setEditingSchedule(null);
		setIsScheduleFormOpen(true);
	};

	const handleEditSchedule = (schedule) => {
		setEditingSchedule(schedule);
		setIsScheduleFormOpen(true);
	};

	const handleScheduleFormSubmit = async (scheduleData) => {
		try {
			let result;
			if (editingSchedule) {
				// Update existing schedule
				result = await dispatch(updateReportSchedule(editingSchedule.scheduleId, scheduleData));
				if (result && result.status) {
					toast.success('Schedule updated successfully');
				} else {
					notifySecurityReportsError(
						FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_UPDATE_FAILED,
						'Security Reports Page / Update Schedule Failed',
						getErrorMessage(result, 'Failed to update schedule')
					);
				}
			} else {
				// Create new schedule
				result = await dispatch(createReportSchedule(scheduleData));
				if (result && result.status) {
					toast.success('Schedule created successfully');
				} else {
					notifySecurityReportsError(
						FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_CREATE_FAILED,
						'Security Reports Page / Create Schedule Failed',
						getErrorMessage(result, 'Failed to create schedule')
					);
				}
			}

			if (result && result.status) {
				// Refresh schedules
				await fetchSchedules();
				setIsScheduleFormOpen(false);
				setEditingSchedule(null);
			}
		} catch (error) {
			notifySecurityReportsError(
				editingSchedule
					? FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_UPDATE_FAILED
					: FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_CREATE_FAILED,
				editingSchedule
					? 'Security Reports Page / Update Schedule Request Error'
					: 'Security Reports Page / Create Schedule Request Error',
				getErrorMessage(error, 'Error saving schedule')
			);
		}
	};

	const handleDeleteSchedule = async (scheduleId, scheduleName) => {
		if (!window.confirm(`Are you sure you want to delete the schedule "${scheduleName}"?`)) {
			return;
		}

		try {
			const result = await dispatch(deleteReportSchedule(scheduleId));
			if (result && result.status) {
				toast.success('Schedule deleted successfully');
				await fetchSchedules();
			} else {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_DELETE_FAILED,
					'Security Reports Page / Delete Schedule Failed',
					getErrorMessage(result, 'Failed to delete schedule')
				);
			}
		} catch (error) {
			notifySecurityReportsError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_DELETE_FAILED,
				'Security Reports Page / Delete Schedule Request Error',
				getErrorMessage(error, 'Error deleting schedule')
			);
		}
	};

	const handleToggleSchedule = async (scheduleId) => {
		try {
			const result = await dispatch(toggleReportSchedule(scheduleId));
			if (result && result.status) {
				toast.success(result.msg || 'Schedule toggled successfully');
				await fetchSchedules();
			} else {
				notifySecurityReportsError(
					FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_TOGGLE_FAILED,
					'Security Reports Page / Toggle Schedule Failed',
					getErrorMessage(result, 'Failed to toggle schedule')
				);
			}
		} catch (error) {
			notifySecurityReportsError(
				FRONTEND_SECURITY_REPORTS_ERROR_CODES.SCHEDULE_TOGGLE_FAILED,
				'Security Reports Page / Toggle Schedule Request Error',
				getErrorMessage(error, 'Error toggling schedule')
			);
		}
	};

	// Filter report history based on report filter
	const filteredReportHistory = reportHistory.filter((report) => {
		if (reportFilter === 'all') return true;
		if (reportFilter === 'manual') return report.reportType === 'Manual';
		if (reportFilter === 'scheduled') return report.reportType && report.reportType.startsWith('Scheduled');
		return true;
	});

	const { pageRows, pageNumber, totalRows, pageLimit, handlePageChange } = usePagination(filteredReportHistory);

	if (hasError) {
		return (
			<Content currentMenu='security-reports' headerTitle='Security Reports'>
				<NetworkError />
				{supportErrorMessage && (
					<div className={classes.supportErrorCard}>
						<strong>Support Error:</strong>
						<code className={classes.supportErrorCode}>{supportErrorMessage}</code>
						<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
							<button
								type='button'
								className={classes.copySupportErrorButton}
								onClick={handleCopySupportError}
							>
								{copiedSupportError ? 'Copied' : 'Copy Error Details'}
							</button>
							<button
								type='button'
								className={classes.copySupportErrorButton}
								onClick={() => clearSecurityReportsSupportError(true)}
							>
								Clear
							</button>
						</div>
					</div>
				)}
			</Content>
		);
	}

	return (
		<Content currentMenu='security-reports' headerTitle='Security Reports'>
			{initialLoading ? (
				<Loader />
			) : (
				<Fragment>
					{supportErrorMessage && (
						<div className={classes.supportErrorBanner}>
							<div className={classes.supportErrorBannerText}>
								<strong>Support Error:</strong>
								<code className={classes.supportErrorCode}>{supportErrorMessage}</code>
							</div>
							<div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
								<button
									type='button'
									className={classes.copySupportErrorButton}
									onClick={handleCopySupportError}
								>
									{copiedSupportError ? 'Copied' : 'Copy Error Details'}
								</button>
								<button
									type='button'
									className={classes.copySupportErrorButton}
									onClick={() => clearSecurityReportsSupportError(true)}
								>
									Clear
								</button>
							</div>
						</div>
					)}
					{/* Controls Section */}
					<div className={classes.filtersContainer}>
						<div className={classes.filterRow}>
						{/* Client Selection — narrows the Watcher dropdown. Shown only when
						    there is more than one client (a provider); a client user has a
						    single company, so it is hidden for them. */}
						{availableClients.length > 1 && (
						<div className={classes.filterBox}>
							<SelectBox
								label='Client'
								value={selectedClient}
								style={{ width: '100%' }}
								onChange={(e) => { setSelectedClient(e.target.value); setSelectedWatchers([]); }}
							>
								<option value=''>All Clients</option>
								{availableClients.map((c) => (
									<option key={c} value={c}>{c}</option>
								))}
							</SelectBox>
						</div>
						)}

						{/* Watcher Selection — filtered to the selected client */}
						<div className={classes.filterBox}>
							<MultiSelectDropdown
								label='Select Watchers'
								options={availableWatchers.filter(w => w.length >= 2 && (!selectedClient || w[2] === selectedClient))}
								selectedValues={selectedWatchers}
								onChange={setSelectedWatchers}
								placeholder='All Watchers'
								displayFormat={(selected) =>
									selected.length === 0
										? 'All Watchers'
										: selected.length === 1
											? availableWatchers.find(w => w[0] === selected[0])?.[1] || '1 selected'
											: `${selected.length} watchers selected`
								}
							/>
						</div>

					{/* Time Period and Subcategories Group */}
					<div className={classes.filterGroup}>
						{/* Time Frame Selection */}
						<div className={classes.filterBox}>
							<SelectBox
								label='Time Period'
								value={timeFrame}
								style={{ width: '100%' }}
								onChange={(e) => setTimeFrame(e.target.value)}
							>
								<option value='7'>Last 7 Days</option>
								<option value='14'>Last 14 Days</option>
								<option value='30'>Last 30 Days</option>
								<option value='60'>Last 60 Days</option>
								<option value='90'>Last 90 Days</option>
								<option value='custom'>Custom Range</option>
							</SelectBox>
						</div>

						{/* Show Subcategories Toggle */}
						<div className={classes.filterBox}>
							<SwitchBox
								label='Show Event Subcategories'
								checked={showSubcategories}
								onChange={(checked) => setShowSubcategories(checked)}
							/>
						</div>
					</div>

					{/* Custom Date Range Pickers */}
					{timeFrame === 'custom' && (
						<>
							<div className={classes.filterBox}>
								<label className={classes.dateLabel}>Start Date</label>
								<input
									type='date'
									className={classes.dateInput}
									value={customStartDate}
									onChange={(e) => setCustomStartDate(e.target.value)}
									max={customEndDate || new Date().toISOString().split('T')[0]}
								/>
							</div>
							<div className={classes.filterBox}>
								<label className={classes.dateLabel}>End Date</label>
								<input
									type='date'
									className={classes.dateInput}
									value={customEndDate}
									onChange={(e) => setCustomEndDate(e.target.value)}
									min={customStartDate}
									max={new Date().toISOString().split('T')[0]}
								/>
							</div>
						</>
					)}

				{/* Email Report Toggle */}
				<div className={classes.filterBox}>
					<SwitchBox
						label='Email Report'
						checked={emailReport}
						onChange={handleEmailToggle}
					/>
					{userEmails.length > 0 && (
						<span className={classes.emailCount}>
							({userEmails.length} email{userEmails.length !== 1 ? 's' : ''} configured)
						</span>
					)}
				</div>

					{/* Action Buttons Group */}
					<div className={classes.buttonGroup}>
						<Button 
							label='Manage Email Recipients'
							onClick={() => setShowEmailModal(true)}
						/>
						<Button 
							label={isGenerating ? 'Generating...' : 'Generate PDF Report'}
							onClick={handleGenerateReport} 
							disabled={isGenerating}
						/>
					</div>
					</div>
				</div>

					{/* Report Schedules Section */}
					<div className={classes.schedulesSection}>
						<div 
							className={classes.sectionHeaderCollapsible}
							onClick={() => setIsSchedulesSectionExpanded(!isSchedulesSectionExpanded)}
						>
							<div className={classes.headerLeft}>
								<span className={classes.toggleIcon}>
									{isSchedulesSectionExpanded ? '▼' : '▶'}
								</span>
								<h2>My Report Schedules</h2>
								{schedules.length > 0 && (
									<span className={classes.scheduleCount}>({schedules.length})</span>
								)}
							</div>
							<div className={classes.headerRight}>
								{isSchedulesSectionExpanded && (
									<Button 
										label="Create Schedule"
										onClick={(e) => {
											e.stopPropagation();
											handleCreateSchedule();
										}}
										variant="primary"
									/>
								)}
							</div>
						</div>

						{isSchedulesSectionExpanded && (
							<>
								{schedules.length > 0 ? (
									<div className={classes.schedulesGrid}>
										{schedules.map((schedule) => (
											<ScheduleCard
												key={schedule.scheduleId}
												schedule={schedule}
												onEdit={handleEditSchedule}
												onDelete={handleDeleteSchedule}
												onToggle={handleToggleSchedule}
											/>
										))}
									</div>
								) : (
									<div className={classes.emptyState}>
										<p>No schedules created yet. Create your first schedule to automate report generation.</p>
										<Button 
											label="Create Your First Schedule"
											onClick={handleCreateSchedule}
											variant="primary"
										/>
									</div>
								)}
							</>
						)}
					</div>

					{/* Schedule Form Modal */}
					<ScheduleForm
						isOpen={isScheduleFormOpen}
						onClose={() => {
							setIsScheduleFormOpen(false);
							setEditingSchedule(null);
						}}
						onSubmit={handleScheduleFormSubmit}
						initialData={editingSchedule}
						availableWatchers={availableWatchers.filter(w => w.length >= 2)}
					/>

					{/* Report History Table */}
					<div className={classes.historySection}>
						<div className={classes.sectionHeader}>
							<h2>Report History</h2>
							<div className={classes.reportFilter}>
								<label>Filter: </label>
								<select 
									value={reportFilter} 
									onChange={(e) => setReportFilter(e.target.value)}
									className={classes.filterSelect}
								>
									<option value="all">All Reports</option>
									<option value="manual">Manual Only</option>
									<option value="scheduled">Scheduled Only</option>
								</select>
							</div>
						</div>
						<Table
							containerStyle={{ height: '95vh', maxHeight: '900px' }}
							tableHead={
								<tr>
									<th>Report Title</th>
									<th>Date Range</th>
									<th>Generated By</th>
									<th>Generated Date</th>
									<th>Type</th>
									<th>Schedule</th>
									<th>Actions</th>
								</tr>
							}
							tableRow={
								pageRows.length > 0 ? (
									pageRows.map((report, index) => (
										<tr key={index}>
											<td>{report.reportTitle || 'Security Report'}</td>
											<td>{report.startDate} to {report.endDate}</td>
											<td>{report.generatedBy}</td>
											<td>{report.generatedDate}</td>
											<td>
												<span className={report.reportType === 'Manual' ? classes.manualBadge : classes.scheduledBadge}>
													{report.reportType}
												</span>
											</td>
											<td>{report.scheduleName || '-'}</td>
											<td>
												<Button 
													label="Download"
													onClick={() => downloadReport(report.reportId)}
												/>
											</td>
										</tr>
									))
								) : (
									<tr>
										<td colSpan='7' style={{ textAlign: 'center' }}>
											{reportFilter === 'all' ? 'No reports generated yet' : 'No reports match the selected filter'}
										</td>
									</tr>
								)
							}
						/>
						<div className='paginationContainer'>
							<Pagination
								onPageChange={handlePageChange}
								rowsPerPage={pageLimit}
								totalPageSize={totalRows}
								pageNumber={pageNumber}
							/>
						</div>
					</div>
				</Fragment>
			)}

			{/* Email Management Modal */}
			<EmailManagementModal
				show={showEmailModal}
				onClose={() => setShowEmailModal(false)}
				userEmails={userEmails}
				onAddEmail={handleAddEmail}
				onDeleteEmail={handleDeleteEmail}
				onSetPrimary={handleSetPrimary}
			/>
		</Content>
	);
};

export default SecurityReports;
